import { UserCircleIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import StatCell from '../../../components/analysis/StatCell';
import { getNormalizedSegments, getSegmentsArray } from '../../../utils/comparativeSegmentsHelper';

export default function ComparativeTimeBreakdownTable({ comparativeData, selectedSubject, comparativeSegmentsMeta }) {
  const [comparativeTimeSpent, setComparativeTimeSpent] = useState(null);
  const [comparativeAccuracy, setComparativeAccuracy] = useState(null);
  const [comparativeQuestionAttempt, setComparativeQuestionAttempt] = useState(null);
  const [userTimeSpent, setUserTimeSpent] = useState(null);
  const [userAccuracy, setUserAccuracy] = useState(null);
  const [userQuestionAttempt, setUserQuestionAttempt] = useState(null);
  const [subjectQuestions, setSubjectQuestions] = useState(null);
  const [segments, setSegments] = useState({});

  useEffect(() => {
    const normalizedSegments = getNormalizedSegments(comparativeSegmentsMeta);
    setSegments(normalizedSegments);
  }, [comparativeSegmentsMeta]);

  useEffect(() => {
    if (!selectedSubject || !comparativeData) return;

    const subjectId = selectedSubject?.id?.toString();
    const getBySubject = (arr) => arr?.find((item) => item.subject_id?.toString() === subjectId);

    setComparativeTimeSpent(getBySubject(comparativeData?.comparativeData?.time_spent));
    setComparativeAccuracy(getBySubject(comparativeData?.comparativeData?.accuracy));
    setComparativeQuestionAttempt(getBySubject(comparativeData?.comparativeData?.test_breakdown));

    setUserTimeSpent(getBySubject(comparativeData?.userData?.time_spent));
    setUserAccuracy(getBySubject(comparativeData?.userData?.accuracy));
    setUserQuestionAttempt(getBySubject(comparativeData?.userData?.test_breakdown));

    setSubjectQuestions(comparativeData?.totalQuestions);
  }, [selectedSubject, comparativeData]);

  if (
    !subjectQuestions ||
    !comparativeTimeSpent ||
    !userTimeSpent ||
    !comparativeAccuracy ||
    !userAccuracy ||
    !comparativeQuestionAttempt ||
    !userQuestionAttempt
  )
    return null;

  if (!segments || Object.keys(segments).length === 0) return null;

  const subjectId = selectedSubject?.id?.toString();
  const totalQ = subjectId === 'overall' ? subjectQuestions?.total : subjectQuestions?.[subjectId];

  const renderStatCell = (top, bottom, color, showPercentage = true) =>
    top !== null && top !== undefined ? (
      <StatCell color={color} top={top} bottom={bottom} showPercentage={showPercentage} />
    ) : (
      <div className="flex items-center justify-center h-8 text-xs text-[var(--q3-neutral-default)]">No data</div>
    );

  const renderTimeCell = (value) =>
    value !== null && value !== undefined ? (
      <>
        {Math.round(value / 60)}
        <span className="ml-1 text-xs text-[var(--q3-neutral-light)]">mins</span>
      </>
    ) : (
      <span className="text-xs text-[var(--q3-neutral-default)]">No data</span>
    );

  const getAttempted = (values, key) =>
    values
      ?.filter((item) => item.title === 'Correct' || item.title === 'Incorrect')
      ?.reduce((sum, item) => sum + Number(item.values?.[key] ?? 0), 0);

  const renderRow = (icon, label, time, attemptTop, accuracy, colorMap) => (
    <tr className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
      <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
        <div className="flex items-center px-4 gap-4 text-[var(--q3-neutral-default)]">
          {icon}
          <p className="truncate text-base font-semibold">{label}</p>
        </div>
      </td>
      <td className="border-r border-[var(--q3-stroke-light)] text-center text-[var(--q3-neutral-default)]">
        {renderTimeCell(time)}
      </td>
      <td className="border-r border-[var(--q3-stroke-light)] text-center text-[var(--q3-neutral-default)]">
        {renderStatCell(attemptTop, totalQ, colorMap.attempt)}
      </td>
      <td className="text-center text-[var(--q3-neutral-default)]">
        {renderStatCell(accuracy, 100, colorMap.accuracy)}
      </td>
    </tr>
  );

  const renderComparativeRow = (segment) => (
    <tr key={segment.key} className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
      <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
        <div className="flex items-center px-4 gap-4 text-[var(--q3-neutral-default)]">
          <segment.icon size={24} />
          <p className="truncate text-base font-semibold">{segment.title}</p>
        </div>
      </td>
      <td className="border-r border-[var(--q3-stroke-light)] text-center text-[var(--q3-neutral-default)]">
        {renderTimeCell(comparativeTimeSpent?.values?.[segment.key])}
      </td>
      <td className="border-r border-[var(--q3-stroke-light)] text-center text-[var(--q3-neutral-default)]">
        {renderStatCell(
          getAttempted(comparativeQuestionAttempt?.values, segment.key),
          totalQ,
          'var(--q3-accent-normal)',
        )}
      </td>
      <td className="text-center text-[var(--q3-neutral-default)]">
        {renderStatCell(comparativeAccuracy?.values?.[segment.key], 100, 'var(--q3-green-normal)')}
      </td>
    </tr>
  );

  const comparativeSegmentsArray = getSegmentsArray(segments, 'topper');

  return (
    <div className="overflow-x-auto rounded-b-xl border border-t-0 border-[var(--q3-stroke-light)]">
      <table className="text-sm w-full">
        <thead className="bg-[var(--q3-surface-dim)]">
          <tr className="h-16">
            <td className="py-3 px-4 w-[318px] text-start border-r border-[var(--q3-stroke-light)]">
              <div className="line-clamp-2 max-w-[300px] leading-snug">Students</div>
            </td>
            {['Time Spent', 'Qs Attempted', 'Accuracy'].map((text, idx, arr) => (
              <td
                key={idx}
                className={`py-3 px-4 min-w-[180px] text-center align-middle ${
                  idx < arr.length - 1 ? 'border-r border-[var(--q3-stroke-light)]' : ''
                }`}
              >
                <div className="line-clamp-2 max-w-[180px] mx-auto leading-snug text-[var(--q3-neutral-default)]">
                  {text}
                </div>
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {renderRow(
            <UserCircleIcon size={24} />,
            'You',
            userTimeSpent?.time_spent,
            userQuestionAttempt?.correct + userQuestionAttempt?.incorrect,
            userAccuracy?.accuracy,
            {
              attempt: 'var(--q3-accent-normal)',
              accuracy: 'var(--q3-green-normal)',
            },
          )}

          {/* Dynamically render comparative segments */}
          {comparativeSegmentsArray.map((segment) => renderComparativeRow(segment))}
        </tbody>
      </table>
    </div>
  );
}
